/* ══════════════════════════════════════════════════════════════
   FII/DII Panel — Institutional Flow Visualization
   Shows today, 5-day, 30-day FII/DII net flows
   ══════════════════════════════════════════════════════════════ */

import { fetchFIIDII } from '../services/market-data';
import { renderUnavailableBlock } from './data-state';

export interface FIIDIIData {
  fiiToday: number;
  diiToday: number;
  fii5d: number;
  dii5d: number;
  fii30d: number;
  dii30d: number;
  fiiBuyingStreak: number;
  asOf?: string;
}

function formatCrore(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  if (Math.abs(value) >= 10000) return `${sign}${(Math.abs(value) / 10000).toFixed(1)}L Cr`;
  return `${sign}₹${Math.abs(value).toLocaleString('en-IN')} Cr`;
}

function flowBar(label: string, fii: number, dii: number): string {
  const net = fii + dii;
  const maxVal = Math.max(Math.abs(fii), Math.abs(dii), 1);
  const fiiWidth = Math.min(Math.abs(fii) / maxVal * 100, 100);
  const diiWidth = Math.min(Math.abs(dii) / maxVal * 100, 100);

  return `
    <div class="fiidii-row">
      <span class="fiidii-label">${label}</span>
      <div class="fiidii-bars">
        <div class="fiidii-bar fii-bar ${fii >= 0 ? 'positive' : 'negative'}" style="width:${fiiWidth}%">
          <span>FII ${formatCrore(fii)}</span>
        </div>
        <div class="fiidii-bar dii-bar ${dii >= 0 ? 'positive' : 'negative'}" style="width:${diiWidth}%">
          <span>DII ${formatCrore(dii)}</span>
        </div>
      </div>
      <span class="fiidii-net ${net >= 0 ? 'positive' : 'negative'}">Net: ${formatCrore(net)}</span>
    </div>`;
}

function renderWithData(container: HTMLElement, d: FIIDIIData): void {
  const streakText = d.fiiBuyingStreak > 0
    ? `🟢 FII buying ${d.fiiBuyingStreak} consecutive days`
    : d.fiiBuyingStreak < 0
      ? `🔴 FII selling ${Math.abs(d.fiiBuyingStreak)} consecutive days`
      : '🟡 Mixed FII activity';
  const sourceLabel = d.asOf ? `Source: NSE • ${d.asOf}` : 'Source: NSE';

  container.innerHTML = `
    <div class="glass-card fiidii-panel">
      <div class="card-header">
        <h3>🏦 FII / DII Flows</h3>
        <span class="fiidii-streak">${streakText}</span>
      </div>
      <div class="fiidii-content">
        ${flowBar('Today', d.fiiToday, d.diiToday)}
        ${flowBar('5-Day', d.fii5d, d.dii5d)}
        ${flowBar('30-Day', d.fii30d, d.dii30d)}
      </div>
      <div class="fiidii-footer">
        <small>${sourceLabel}</small>
      </div>
    </div>`;
}

export function renderFIIDIIPanel(container: HTMLElement, data?: FIIDIIData): void {
  if (data) {
    renderWithData(container, data);
  } else {
    container.innerHTML = renderUnavailableBlock('glass-card fiidii-panel', 'Loading FII/DII flows...');
    fetchFIIDII().then(apiData => {
      if (apiData) {
        const mapped: FIIDIIData = {
          fiiToday: apiData.latest?.fiiNetValue ?? 0,
          diiToday: apiData.latest?.diiNetValue ?? 0,
          fii5d: apiData.fiveDay?.fiiNet ?? 0,
          dii5d: apiData.fiveDay?.diiNet ?? 0,
          fii30d: apiData.thirtyDay?.fiiNet ?? 0,
          dii30d: apiData.thirtyDay?.diiNet ?? 0,
          fiiBuyingStreak: apiData.streak?.fii ?? 0,
          asOf: apiData.latest?.date,
        };
        renderWithData(container, mapped);
      } else {
        container.innerHTML = renderUnavailableBlock('glass-card fiidii-panel', 'Live FII/DII feed unavailable');
      }
    }).catch(() => {
      container.innerHTML = renderUnavailableBlock('glass-card fiidii-panel', 'Live FII/DII feed unavailable');
    });
  }
}
